import {formatInTimezone} from "@/composables/useTimezone";

// Shift times are shown in the fleet's timezone, the one the API cuts days
// in and sends back with each list and shift. A timesheet is read against
// the fleet's working day, whoever is looking at it.
export const shiftFormatter = (tz) => {
    const fmtTime = (ts) => ts
        ? formatInTimezone(ts, {year: undefined, month: undefined, day: undefined, second: undefined}, tz)
        : "";
    const fmtDay = (ts) => ts
        ? formatInTimezone(ts, {
            weekday: "short", year: undefined, month: "short", day: "numeric",
            hour: undefined, minute: undefined, second: undefined,
        }, tz)
        : "";
    const fmtDayTime = (ts) => ts
        ? formatInTimezone(ts, {weekday: "short", year: undefined, month: "short", day: "numeric", second: undefined}, tz)
        : "";
    const dayKey = (ts) => formatInTimezone(ts, {hour: undefined, minute: undefined, second: undefined}, tz);
    // "6:00 AM", or "6:00 AM (next day)" when it falls on a later day than
    // `fromTs`, so an overnight shift does not read as running backwards.
    const fmtTimeAfter = (ts, fromTs) => {
        if (!ts) return "";
        const t = fmtTime(ts);
        return fromTs && dayKey(ts) !== dayKey(fromTs) ? `${t} (next day)` : t;
    };

    return {fmtTime, fmtDay, fmtDayTime, fmtTimeAfter};
};

export const fmtDuration = (seconds) => {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    return `${m}m`;
};

// Status of a shift as one label and colour, most urgent first.
export const shiftStatus = (s) => {
    if (s.status === "on_shift") return {label: "On shift", color: "success", icon: "mdi-circle-medium"};
    if (s.status === "on_break") return {label: "On break", color: "warning", icon: "mdi-coffee"};
    if (s.needs_review || s.can_review) return {label: "Needs review", color: "error", icon: "mdi-alert-circle"};
    if (s.reviewed_at) return {label: "Reviewed", color: "primary", icon: "mdi-check-decagram"};
    if (s.source === "auto") return {label: "Auto-closed", color: "grey", icon: "mdi-timer-off-outline"};
    return {label: "Completed", color: "success", icon: "mdi-check"};
};

// Why the server flagged a shift, in words a manager can act on.
export const reasonText = (r, fmtDayTime) => {
    const at = r.at ? fmtDayTime(r.at) : "";
    switch (r.code) {
        case "reflagged":
            return at
                ? `Flagged again after the last review: the driver's phone sent another clock-out, ${at}.`
                : "Flagged again after the last review.";
        case "no_activity":
            return `The driver never clocked out and nothing showed they were working, so the system ended the shift at ${at}.`;
        case "trip_only":
            return `The driver never clocked out. The system ended the shift at ${at}, after a vehicle trip that began after the driver's last own action. Check who was driving.`;
        case "late_claim":
            return `The driver's clock-out of ${at} was not applied automatically: it arrived too late, or is later than anything else recorded.`;
        case "other_claim":
            return `The phone also sent a different clock-out: ${at}.`;
        case "later_clock_in":
            return `The phone clocked in again at ${at} while this shift was still open. A clock-out probably never reached us.`;
        case "pulled_back":
            return `The system's end was moved back to ${at} because the driver's next shift started earlier.`;
        case "backdated_start":
            return `The clock-in only reached us at ${at}, well after the time it says it happened.`;
        case "start_moved":
            return `The phone says the shift started at ${at}, which is not the recorded start.`;
        case "clock_drift":
            return "The phone's clock disagreed with this shift's times.";
        case "closed_at_start":
            return `The phone sent a clock-out (${at}) at or before the start, so the shift was closed with no length.`;
        case "late_entry":
            return "A break or clock time reached us long after it happened.";
        default:
            return "Something about this shift needs a manager's look.";
    }
};

// Read an API error as one line for a snackbar or an inline alert.
export const apiError = (e, fallback) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    // A role without the permission is answered 400 "Action not permitted!"
    // by the API's permission gate, and a company mismatch 403.
    if (status === 403 || (status === 400 && /not permitted/i.test(data?.message || ""))) {
        return data?.message && status === 403 && !/unauthori[sz]ed|not permitted/i.test(data.message)
            ? data.message
            : "You don't have access to this. Ask your administrator.";
    }
    const first = data?.errors && Object.values(data.errors)[0];
    return (Array.isArray(first) ? first[0] : null) || data?.message || fallback;
};

// ---- date range presets --------------------------------------------------
// Days are the fleet's days: "today" is today in the company's zone, the one
// the API cuts the range in. Weeks start on Monday, as the driver app's own
// week does (Carbon startOfWeek on the API).

export const RANGE_PRESETS = [
    {value: "today", title: "Today"},
    {value: "yesterday", title: "Yesterday"},
    {value: "this_week", title: "This week"},
    {value: "last_week", title: "Last week"},
    {value: "this_month", title: "This month"},
    {value: "last_month", title: "Last month"},
    {value: "custom", title: "Custom"},
    // No range: where the review queue opens, so an old flag is never missed.
    {value: "all", title: "All dates"},
];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

// Today's date in `tz` as a UTC-midnight Date, for plain day arithmetic.
const todayIn = (tz) => {
    let s;
    try {
        s = new Intl.DateTimeFormat("en-CA", {timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit"})
            .format(new Date());
    } catch {
        s = new Intl.DateTimeFormat("en-CA", {year: "numeric", month: "2-digit", day: "2-digit"}).format(new Date());
    }
    const [y, m, d] = s.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
};

const addDays = (d, n) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));

// {from, to} as YYYY-MM-DD for a preset, or null for "custom".
export const presetRange = (preset, tz) => {
    const today = todayIn(tz);
    // Monday = 0.
    const monday = addDays(today, -((today.getUTCDay() + 6) % 7));
    const y = today.getUTCFullYear();
    const m = today.getUTCMonth();
    switch (preset) {
        case "today":
            return {from: ymd(today), to: ymd(today)};
        case "yesterday":
            return {from: ymd(addDays(today, -1)), to: ymd(addDays(today, -1))};
        case "last_7":
            return {from: ymd(addDays(today, -6)), to: ymd(today)};
        case "last_30":
            return {from: ymd(addDays(today, -29)), to: ymd(today)};
        case "this_week":
            return {from: ymd(monday), to: ymd(addDays(monday, 6))};
        case "last_week":
            return {from: ymd(addDays(monday, -7)), to: ymd(addDays(monday, -1))};
        case "this_month":
            return {from: ymd(new Date(Date.UTC(y, m, 1))), to: ymd(new Date(Date.UTC(y, m + 1, 0)))};
        case "last_month":
            return {from: ymd(new Date(Date.UTC(y, m - 1, 1))), to: ymd(new Date(Date.UTC(y, m, 0)))};
        case "all":
            return {from: "", to: ""};
        default:
            return null;
    }
};

const isYmd = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(`${s}T00:00:00Z`));
export const validYmd = isYmd;

// "Oct 3", or "Oct 3, 2025" outside the current year.
const shortDay = (s) => {
    const d = new Date(`${s}T00:00:00Z`);
    const opts = {month: "short", day: "numeric", timeZone: "UTC"};
    if (d.getUTCFullYear() !== new Date().getUTCFullYear()) opts.year = "numeric";
    return new Intl.DateTimeFormat("en-US", opts).format(d);
};

// The range in words, for an empty state: "last week", "on Oct 3",
// "from Oct 1 to Oct 9".
export const rangePhrase = (preset, from, to) => {
    const named = {
        today: "today", yesterday: "yesterday", this_week: "this week",
        last_week: "last week", this_month: "this month", last_month: "last month",
        last_7: "in the last 7 days", last_30: "in the last 30 days",
    };
    if (named[preset]) return named[preset];
    if (from && to) return from === to ? `on ${shortDay(from)}` : `from ${shortDay(from)} to ${shortDay(to)}`;
    if (from) return `since ${shortDay(from)}`;
    if (to) return `up to ${shortDay(to)}`;
    return "";
};

// The status filter: the API's status values, plus the two lists it always
// had. "On shift now" ignores the dates; "Needs review" is cut by them, so
// its count and its list are the same search.
export const STATUS_FILTERS = [
    {value: "all", title: "All statuses", noun: "shifts"},
    {value: "open", title: "On shift now", noun: "running shifts", queue: true},
    {value: "review", title: "Needs review", noun: "shifts to review"},
    {value: "on_shift", title: "On shift", noun: "shifts on shift"},
    {value: "on_break", title: "On break", noun: "shifts on a break"},
    {value: "completed", title: "Completed by the driver", noun: "completed shifts"},
    {value: "auto_closed", title: "Auto-closed", noun: "auto-closed shifts"},
    {value: "edited", title: "Edited by the office", noun: "shifts edited by the office"},
];
